import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import '../ManagedServices/ManagedCDN.css';

const AuroraDB = () => {
    return (
        <>
            <Helmet>
                {/* Primary Meta Tags */}
                <title>Managed Amazon Aurora Database - Expert DBA Services | Epicfinal</title>
                <meta name="title" content="Managed Amazon Aurora Database - Expert DBA Services | Epicfinal" />
                <meta name="description" content="Managed Amazon Aurora database service. Full setup, 24/7 monitoring, query optimization, backup management, security patches & performance tuning. Expert DBA support included." />
                <meta name="keywords" content="Amazon Aurora, managed Aurora, Aurora database, AWS Aurora management, database management, Aurora MySQL, Aurora PostgreSQL, DBA services, database optimization, query tuning, database backup, Aurora monitoring, managed database, cloud database" />
                <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
                <meta name="author" content="Epicfinal" />
                <link rel="canonical" href="https://epicfinal.com/aurora-database" />

                {/* Language & Locale */}
                <html lang="en" />
                <meta property="og:locale" content="en_US" />
                <link rel="alternate" href="https://epicfinal.com/aurora-database" hrefLang="en" />
                <link rel="alternate" href="https://epicfinal.com/aurora-database" hrefLang="x-default" />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://epicfinal.com/aurora-database" />
                <meta property="og:title" content="Managed Amazon Aurora Database | Epicfinal" />
                <meta property="og:description" content="Expert Aurora database management. Setup, monitoring, query optimization & backup management with 24/7 DBA support." />
                <meta property="og:image" content="https://epicfinal.com/android-chrome-512x512.png" />
                <meta property="og:image:alt" content="Epicfinal Managed Aurora - Expert Database Administration" />
                <meta property="og:site_name" content="Epicfinal" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:url" content="https://epicfinal.com/aurora-database" />
                <meta name="twitter:title" content="Managed Amazon Aurora Database | Epicfinal" />
                <meta name="twitter:description" content="Expert Aurora database management with 24/7 monitoring & DBA support." />
                <meta name="twitter:image" content="https://epicfinal.com/android-chrome-512x512.png" />

                {/* Schema.org Structured Data */}
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "Service",
                        "name": "Managed Amazon Aurora Database",
                        "description": "Full management of Amazon Aurora database instances including setup, monitoring, optimization, backup management, and performance tuning",
                        "provider": {"@type": "Organization", "name": "Epicfinal", "url": "https://epicfinal.com"},
                        "serviceType": "Database Administration",
                        "areaServed": "Worldwide",
                        "url": "https://epicfinal.com/aurora-database"
                    }
                `}</script>
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        "name": "Managed Amazon Aurora Database",
                        "url": "https://epicfinal.com/aurora-database",
                        "breadcrumb": {
                            "@type": "BreadcrumbList",
                            "itemListElement": [
                                {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://epicfinal.com"},
                                {"@type": "ListItem", "position": 2, "name": "Managed Services", "item": "https://epicfinal.com/managed-services"},
                                {"@type": "ListItem", "position": 3, "name": "Aurora Database", "item": "https://epicfinal.com/aurora-database"}
                            ]
                        }
                    }
                `}</script>
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": [
                            {"@type": "Question", "name": "What Aurora database management services do you provide?", "acceptedAnswer": {"@type": "Answer", "text": "We provide complete Aurora management including database deployment, parameter optimization, read replica setup, query performance analysis, index optimization, backup management, security patches, and 24/7 monitoring."}},
                            {"@type": "Question", "name": "Do you support both Aurora MySQL and Aurora PostgreSQL?", "acceptedAnswer": {"@type": "Answer", "text": "Yes, we manage both Aurora MySQL and Aurora PostgreSQL database instances, including configuration optimization specific to each engine."}},
                            {"@type": "Question", "name": "How is Aurora database management priced?", "acceptedAnswer": {"@type": "Answer", "text": "Aurora database management is custom-priced based on your specific requirements including database size, complexity, and SLA needs. Contact us for a personalized quote."}}
                        ]
                    }
                `}</script>
            </Helmet>

            <div className="managed-cdn-page">
                <section className="cdn-hero">
                    <div className="container">
                        <div className="cdn-hero-content">
                            <div className="cdn-icon-badge">
                                <i className="las la-database"></i>
                            </div>
                            <h1 className="cdn-hero-title">
                                Managed Amazon Aurora Database
                            </h1>
                            <p className="cdn-hero-subtitle">
                                Expert database management for Amazon Aurora
                            </p>
                            <p className="cdn-hero-description">
                                Full management of your Amazon Aurora database instance – including setup, monitoring, optimization, backup management, and performance tuning, ensuring your database runs smoothly and efficiently.
                            </p>
                            <div className="cdn-hero-buttons">
                                <a href="https://my.epicfinal.com/index.php?rp=/store/managed-services/managed-amazon-aurora-database" 
                                   className="btn-cdn btn-primary" target="_blank" rel="noopener noreferrer">
                                    Get Custom Quote
                                </a>
                                <Link to="/contact1" className="btn-cdn btn-secondary">
                                    Talk to Expert
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="cdn-features">
                    <div className="container">
                        <div className="cdn-section-header">
                            <h2>Professional Database Management</h2>
                            <p>Keep your Aurora database optimized and secure</p>
                        </div>
                        <div className="cdn-features-grid">
                            <div className="cdn-feature-card">
                                <div className="cdn-feature-icon">
                                    <i className="las la-cogs"></i>
                                </div>
                                <h3>Database Setup</h3>
                                <p>Complete Aurora database configuration and deployment optimized for your workload.</p>
                            </div>
                            <div className="cdn-feature-card">
                                <div className="cdn-feature-icon">
                                    <i className="las la-chart-line"></i>
                                </div>
                                <h3>Performance Monitoring</h3>
                                <p>Continuous monitoring of queries, connections, and resource utilization.</p>
                            </div>
                            <div className="cdn-feature-card">
                                <div className="cdn-feature-icon">
                                    <i className="las la-tachometer-alt"></i>
                                </div>
                                <h3>Query Optimization</h3>
                                <p>Identify and optimize slow queries for improved database performance.</p>
                            </div>
                            <div className="cdn-feature-card">
                                <div className="cdn-feature-icon">
                                    <i className="las la-database"></i>
                                </div>
                                <h3>Backup Management</h3>
                                <p>Automated backup configuration, monitoring, and recovery procedures.</p>
                            </div>
                            <div className="cdn-feature-card">
                                <div className="cdn-feature-icon">
                                    <i className="las la-shield-alt"></i>
                                </div>
                                <h3>Security Updates</h3>
                                <p>Regular security patches and compliance management for your database.</p>
                            </div>
                            <div className="cdn-feature-card">
                                <div className="cdn-feature-icon">
                                    <i className="las la-tools"></i>
                                </div>
                                <h3>Maintenance</h3>
                                <p>Scheduled maintenance windows with minimal disruption to your services.</p>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="cdn-included">
                    <div className="container">
                        <div className="cdn-section-header">
                            <h2>What's Included</h2>
                            <p>Comprehensive Aurora database management</p>
                        </div>
                        <div className="cdn-included-grid">
                            <div className="cdn-included-column">
                                <h3><i className="las la-check-circle"></i> Setup & Configuration</h3>
                                <ul>
                                    <li><i className="las la-angle-right"></i> Database deployment</li>
                                    <li><i className="las la-angle-right"></i> Parameter optimization</li>
                                    <li><i className="las la-angle-right"></i> Read replica setup</li>
                                    <li><i className="las la-angle-right"></i> Connection pooling</li>
                                </ul>
                            </div>
                            <div className="cdn-included-column">
                                <h3><i className="las la-check-circle"></i> Performance & Optimization</h3>
                                <ul>
                                    <li><i className="las la-angle-right"></i> Query performance analysis</li>
                                    <li><i className="las la-angle-right"></i> Index optimization</li>
                                    <li><i className="las la-angle-right"></i> Resource tuning</li>
                                    <li><i className="las la-angle-right"></i> Bottleneck resolution</li>
                                </ul>
                            </div>
                            <div className="cdn-included-column">
                                <h3><i className="las la-check-circle"></i> Security & Maintenance</h3>
                                <ul>
                                    <li><i className="las la-angle-right"></i> Security patches</li>
                                    <li><i className="las la-angle-right"></i> Backup management</li>
                                    <li><i className="las la-angle-right"></i> monitoring</li>
                                    <li><i className="las la-angle-right"></i> Expert support</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="cdn-pricing">
                    <div className="container">
                        <div className="cdn-pricing-card">
                            <div className="cdn-pricing-header">
                                <h2>Aurora Database Management</h2>
                                <p>Custom pricing based on your requirements</p>
                            </div>
                            <div className="cdn-pricing-body">
                                <div className="cdn-pricing-amount">
                                    <span className="cdn-price">Custom</span>
                                    <span className="cdn-period">Contact for Quote</span>
                                </div>
                                <ul className="cdn-pricing-features">
                                    <li><i className="las la-check"></i> Aurora database setup & configuration</li>
                                    <li><i className="las la-check"></i> Continuous performance monitoring</li>
                                    <li><i className="las la-check"></i> Query optimization</li>
                                    <li><i className="las la-check"></i> Backup management</li>
                                    <li><i className="las la-check"></i> Security updates & patches</li>
                                    <li><i className="las la-check"></i> High availability configuration</li>
                                    <li><i className="las la-check"></i> database support</li>
                                    <li><i className="las la-check"></i> Monthly performance reports</li>
                                </ul>
                                <a href="https://my.epicfinal.com/index.php?rp=/store/managed-services/managed-amazon-aurora-database" 
                                   className="cdn-pricing-btn" target="_blank" rel="noopener noreferrer">
                                    Request Quote
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="cdn-cta">
                    <div className="container">
                        <div className="cdn-cta-content">
                            <h2>Ready to Optimize Your Aurora Database?</h2>
                            <p>Let our database experts manage your Aurora infrastructure</p>
                            <div className="cdn-cta-buttons">
                                <a href="https://my.epicfinal.com/index.php?rp=/store/managed-services/managed-amazon-aurora-database" 
                                   className="btn-cdn btn-light" target="_blank" rel="noopener noreferrer">
                                    Request Quote
                                </a>
                                <a href="https://meetings.hubspot.com/viacheslav-seifer" 
                                   className="btn-cdn btn-outline" target="_blank" rel="noopener noreferrer">
                                    Schedule Consultation
                                </a>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};

export default AuroraDB;

